import React, { useState, useMemo, useEffect } from 'react';
import type { User } from '@supabase/supabase-js';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { AdminLayout } from '../AdminLayout';
import { Program, Enrollment, ProgramType, getProgramTypeDisplayName, getProgramTypeIcon, getProgramTypeSectionColor, compareProgramTypes, selectedDaysOf, optInItemsOf } from '../../types/program';
import { formatDateDisplay, formatDateTimeInBusinessTZ } from '../../utils/date';
import { softUnenrollEnrollment } from '../utils/unenroll';
import { Modal } from '../../components/Modal';
import { DaySelector } from '../../components/DaySelector';
import AdminFlexPlanSection from '../components/AdminFlexPlanSection';
import { EnrollmentDetailPanel } from '../components/EnrollmentDetailPanel';
import { ProgramTypeScrollNav, useProgramTypeSectionNav } from '../../components/ProgramTypeScrollNav';
import {
  BreakdownRow,
  buildProgramSummaries,
  buildPivotData,
  collectBreakdownLabels,
} from './enrollmentBreakdown';
import { trackEvent, analytics, subjectMetadata } from '../../utils/analytics';
import { Copy, Edit, UserMinus, PowerOff } from 'lucide-react';
import { getErrorMessage } from '../../utils/errorMessage';
import { useUrlTabState } from '../../hooks/useUrlTabState';
import { sessionDayPredicate } from '../../utils/programSessionDates';
import { BRAND_PROFILES } from '../../config/brandProfiles';
import { AdminBrandTag } from '../components/AdminBrandTag';
import { BrandFilterSelect } from '../components/BrandFilterSelect';
import { BRAND_FILTER_VALUES, matchesBrandFilter, type BrandFilter } from '../utils/brandFilter';
import { FocusedRecordBanner } from '../components/FocusedRecordBanner';
import { ENROLLMENT_PARAM, useFocusedRecordId } from '../utils/recordLinks';

type EnrollmentsView = 'summary' | 'details';
const ENROLLMENTS_VIEWS: readonly EnrollmentsView[] = ['summary', 'details'];

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  metadata?: {
    school?: string;
    grade?: string;
  };
  active: boolean;
  student_parents?: Array<{
    parent_id: string;
    relationship: string;
    is_primary: boolean;
    parents: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      phone: string | null;
    };
  }>;
}


interface Invoice {
  id: string;
  enrollment_id: string;
  total_amount: number;
  amount_paid: number;
  status: 'pending' | 'partial' | 'paid' | 'overdue' | 'voided';
  due_date: string;
  period_start: string;
  period_end: string;
  memo_code?: string;
  created_at: string;
  updated_at: string;
  payments: Payment[];
}

interface Payment {
  id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  status: string;
}

// Lightweight latest-invoice summary returned inline by search_enrollments.
// The full invoice history is fetched lazily on row expand via enrollment_invoices.
interface LatestInvoice {
  id: string;
  status: string;
  amount_paid: number;
  total_amount: number;
  due_date: string | null;
}

interface EnrollmentWithDetails extends Enrollment {
  students: Student;
  programs: Program;
  // Payment status is precomputed server-side (enrollment_payment_status) so the
  // list payload no longer carries the full invoices array.
  payment_status?: string;
  latest_invoice?: LatestInvoice | null;
}

/**
 * Mutation inputs carry the student and program names, not just ids, so the audit event can
 * be written with them. See `subjectMetadata`: an unenroll cascade deletes the enrollment,
 * and a read-time lookup then has nothing left to name.
 */
interface EnrollmentSubjectVars {
  studentName?: string | null;
  programName?: string | null;
}

interface EnrollmentToggleVars extends EnrollmentSubjectVars {
  enrollmentId: string;
  isActive: boolean;
}

interface EnrollmentEditVars extends EnrollmentSubjectVars {
  enrollmentId: string;
  updates: Partial<Enrollment>;
}

// Helper function to calculate age from date of birth
const calculateAge = (dateOfBirth: string | null): string => {
  if (!dateOfBirth) return 'Not provided';
  
  try {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age.toString();
  } catch (error) {
    return 'Not provided';
  }
};

// Helper function to truncate program names to first 3 words (excluding symbols)
const truncateProgramName = (programName: string): string => {
  if (!programName) return '';
  
  // Remove symbols and split into words
  const words = programName
    .replace(/[^\w\s]/g, '') // Remove symbols but keep letters, numbers, and spaces
    .split(/\s+/)
    .filter(word => word.length > 0); // Remove empty strings
  
  if (words.length <= 3) {
    return programName;
  }
  
  // Take first 3 words and add ellipsis
  return words.slice(0, 3).join(' ') + '...';
};

// Returns null when no meaningful school name is available (used in display-only contexts)
const displaySchool = (schoolName: string | null | undefined): string | null => {
  if (!schoolName || schoolName === 'Not provided') return null;
  return schoolName
    .replace(/\s+Elementary\s+School$/i, '')
    .replace(/\s+School$/i, '')
    .replace(/\s+Elementary$/i, '')
    .trim() || null;
};

// Returns null when no meaningful grade is available (used in display-only contexts)
const displayGrade = (grade: string | null | undefined): string | null => {
  if (!grade || grade === 'Not provided') return null;
  return /^\d+$/.test(grade) ? `G${grade}` : grade;
};

// Helper function to shorten school names by removing common suffixes
const shortenSchoolName = (schoolName: string | null | undefined): string => {
  if (!schoolName || schoolName === 'Not provided') return 'Not provided';
  
  // Remove common suffixes
  const shortened = schoolName
    .replace(/\s+Elementary\s+School$/i, '')
    .replace(/\s+School$/i, '')
    .replace(/\s+Elementary$/i, '');
  
  return shortened;
};

// Helper function to format grade display
const formatGradeDisplay = (grade: string | null | undefined): string => {
  if (!grade || grade === 'Not provided') return 'Not provided';
  
  // Only add "G" prefix if the grade is purely numeric (digits only)
  if (/^\d+$/.test(grade)) {
    return `G${grade}`;
  }
  
  return grade;
};

// Payment statuses that indicate money has moved — fully paid, partially paid, or
// an in-flight (processing) payment. These block editing/unenrolling, mirroring the
// old "any amount paid OR any processing payment" check now that the list payload
// carries a precomputed payment_status instead of the full invoices array.
const hasBlockingPayment = (status: string | null | undefined): boolean =>
  status === 'paid' || status === 'partial' || status === 'payment_processing';

// Helper function to copy enrollment ID to clipboard
const copyEnrollmentId = (enrollmentId: string) => {
  navigator.clipboard.writeText(enrollmentId);
  alert('Enrollment ID copied to clipboard!');
};

export function AdminEnrollments() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setCurrentUser(data.user));
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<string | null>(null);
  const [isEditingEnrollment, setIsEditingEnrollment] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<EnrollmentWithDetails | null>(null);
  const [isUnenrollModalOpen, setIsUnenrollModalOpen] = useState(false);
  const [enrollmentToUnenroll, setEnrollmentToUnenroll] = useState<EnrollmentWithDetails | null>(null);
  const [selectedProgramFilter, setSelectedProgramFilter] = useState<string | null>(null);
  const [viewMode, setViewMode] = useUrlTabState<EnrollmentsView>('view', ENROLLMENTS_VIEWS, 'summary');
  // `?enrollment=<id>` (from search, or a pasted link) narrows the Details list to that one
  // enrollment, whatever the filters say; typing a search lets go of it.
  const [focusedEnrollmentId, clearFocusedEnrollment] = useFocusedRecordId(ENROLLMENT_PARAM);
  // Arriving on a link opens that enrollment's row.
  useEffect(() => {
    if (focusedEnrollmentId) setSelectedEnrollmentId(focusedEnrollmentId);
  }, [focusedEnrollmentId]);
  // Scopes both views. An enrollment's brand is its program's; both RPCs embed it.
  const [brandFilter, setBrandFilter] = useUrlTabState<BrandFilter>('brand', BRAND_FILTER_VALUES, 'all');
  const [showInactivePrograms, setShowInactivePrograms] = useState<boolean>(false);
  const [showInactiveEnrollments, setShowInactiveEnrollments] = useState<boolean>(false);
  const [excludeVoided, setExcludeVoided] = useState<boolean>(false);
  const [summaryNameFilter, setSummaryNameFilter] = useState('');
  // Bulk deactivate state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState<boolean>(false);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState<boolean>(false);
  const [, setBulkPreviewProgramsCount] = useState<number | null>(null);
  const [bulkPreviewEnrollmentsCount, setBulkPreviewEnrollmentsCount] = useState<number | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkProgramBreakdown, setBulkProgramBreakdown] = useState<Array<{ programId: string; programName: string; activeEnrollmentCount: number }>>([]);
  
  // Edit enrollment form state
  const [editFormData, setEditFormData] = useState<{
    enrollment_start_date: string;
    selected_days: string[];
    opt_in_items: string[];
  }>({
    enrollment_start_date: '',
    selected_days: [],
    opt_in_items: []
  });
  
  const queryClient = useQueryClient();


  // Mutation to toggle enrollment active status
  const toggleEnrollmentStatus = useMutation({
    mutationFn: async ({ enrollmentId, isActive }: EnrollmentToggleVars) => {
      const { error } = await supabase
        .from('enrollments')
        .update({ is_active: isActive })
        .eq('id', enrollmentId);
      
      if (error) throw error;
      return { enrollmentId, isActive };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-enrollments'] });
      trackEvent({
        eventType: 'admin_enrollment_status_toggle',
        user: currentUser,
        entityType: 'enrollment',
        entityId: variables.enrollmentId,
        success: true,
        metadata: { ...subjectMetadata(variables), action: variables.isActive ? 'activate' : 'deactivate' }
      });
    },
    onError: (error, variables) => {
      console.error('Failed to toggle enrollment status:', error);
      trackEvent({
        eventType: 'admin_enrollment_status_toggle',
        user: currentUser,
        entityType: 'enrollment',
        entityId: variables.enrollmentId,
        success: false,
        errorMessage: getErrorMessage(error),
        metadata: { ...subjectMetadata(variables), action: variables.isActive ? 'activate' : 'deactivate' }
      });
    }
  });

  // Summary breakdowns come from a server-side aggregation RPC. It returns a small
  // pre-grouped result (program × school × grade × is_active × payment_status) that
  // is never row-capped, so per-program/grade/school counts are correct at any scale.
  // The display toggles are applied client-side over this small set (no refetch).
  const { data: breakdown, isLoading: breakdownLoading, error: breakdownError } =
    useQuery<BreakdownRow[]>({
      queryKey: ['admin-enrollment-breakdown'],
      queryFn: async () => {
        const { data, error } = await supabase.rpc('enrollment_breakdown');
        if (error) throw error;
        const rows = (Array.isArray(data) ? data : []) as Array<Record<string, unknown>>;
        return rows.map((r) => ({
          program: r.program as Program,
          school: (r.school as string) ?? 'Not provided',
          grade: (r.grade as string) ?? 'Not provided',
          is_active: Boolean(r.is_active),
          payment_status: (r.payment_status as string) ?? 'no_invoice',
          cnt: Number(r.cnt) || 0,
        }));
      },
    });

  // Fetch enrollment rows for the Details list via search_enrollments RPC.
  // The payload is now lightweight: payment_status is precomputed server-side and
  // only a latest-invoice summary is embedded (full history is lazy-loaded on
  // expand), so we can fetch a high limit of thin rows. Only loaded when the
  // Details view is open — the Summary view is driven entirely by the breakdown.
  // A linked enrollment may be inactive, so a link loads the inactive ones too — the same
  // rows the "show inactive" toggle loads.
  const loadInactiveEnrollments = showInactiveEnrollments || focusedEnrollmentId !== null;
  const { data: enrollments, isLoading: enrollmentsLoading, error: enrollmentsError } =
    useQuery<EnrollmentWithDetails[]>({
      queryKey: ['admin-enrollments', { showInactive: loadInactiveEnrollments }],
      placeholderData: keepPreviousData,
      enabled: viewMode === 'details',
      queryFn: async () => {
        const { data, error } = await supabase.rpc('search_enrollments', {
          p_is_active: loadInactiveEnrollments ? null : true,
          p_program_id: null,
          p_limit: 5000,
          p_offset: 0,
          p_sort_direction: 'desc',
        });
        if (error) throw error;
        const rows = (Array.isArray(data) ? data : []) as Array<{
          enrollment_data: Record<string, unknown>;
          total_count: number;
        }>;
        // No hydration step. `metadata` is where these live, and copying them onto
        // top-level fields here is what let the rest of the page believe in a column.
        return rows.map(({ enrollment_data: r }) => r as unknown as EnrollmentWithDetails);
      },
    });

  // `search_enrollments` embeds a thin program — no brand, no no-class dates, no selective
  // dates — so the per-day editor reads the calendar fields itself. Without them it offered
  // every cron weekday, including days the program does not meet (BHCS-133).
  const perDayProgramId =
    isEditingEnrollment && selectedEnrollment?.programs.billing_type === 'per_day'
      ? selectedEnrollment.programs.id
      : null;
  const { data: perDayProgramCalendar } = useQuery({
    queryKey: ['program-session-calendar', perDayProgramId],
    enabled: perDayProgramId !== null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('programs')
        .select('type, schedule_cron, start_date, end_date, excluded_dates, selective_dates, brand_key')
        .eq('id', perDayProgramId ?? '')
        .single();
      if (error) throw error;
      return data;
    },
  });


  // The three Summary display toggles, bundled for the pure breakdown helpers.
  const breakdownToggles = useMemo(
    () => ({ showInactivePrograms, showInactiveEnrollments, excludeVoided }),
    [showInactivePrograms, showInactiveEnrollments, excludeVoided]
  );

  // Summary structures are derived from the server-side breakdown via pure helpers
  // (see enrollmentBreakdown.ts, unit-tested). Toggles are applied client-side.
  // Brand is applied before the helpers, not beside the toggles, so every count they derive --
  // per program, per school, per grade, and the totals -- is one brand's when one is picked.
  const brandBreakdown = useMemo(
    () => (breakdown ?? []).filter(row => matchesBrandFilter(row.program.brand_key, brandFilter)),
    [breakdown, brandFilter]
  );

  const programSummaries = useMemo(
    () => buildProgramSummaries(brandBreakdown, breakdownToggles),
    [brandBreakdown, breakdownToggles]
  );

  const pivotData = useMemo(
    () => buildPivotData(brandBreakdown, breakdownToggles),
    [brandBreakdown, breakdownToggles]
  );

  // Program types present in the Summary pivot (respecting the name filter), ordered
  // like the section separators. Feeds the sticky horizontal category nav.
  const summaryTypes = useMemo(() => {
    const filtered = summaryNameFilter
      ? pivotData.filter(row => row.program.name.toLowerCase().includes(summaryNameFilter.toLowerCase()))
      : pivotData;
    const present = Array.from(new Set(filtered.map(row => (row.program.type || 'other') as string)));
    return present.sort(compareProgramTypes);
  }, [pivotData, summaryNameFilter]);

  const summaryTypeNav = useProgramTypeSectionNav(summaryTypes);

  // Consistent school / grade column sets for the Summary pivot. Labels are already
  // normalized server-side in the breakdown rows.
  const { schools: allSchools, grades: allGrades } = useMemo(
    () => collectBreakdownLabels(breakdown ?? [], breakdownToggles),
    [breakdown, breakdownToggles]
  );

  // Unenroll mutation — soft-deactivate, never hard-delete (see softUnenrollEnrollment).
  const unenrollMutation = useMutation({
    mutationFn: async (enrollment: EnrollmentWithDetails) => {
      await softUnenrollEnrollment(supabase, enrollment.id);
      // The parent-initiated unenroll has always been logged; this side was silent, so a row
      // going inactive had no attributable cause. Names are snapshotted, not looked up later.
      analytics.trackAdminUnenroll({
        enrollmentId: enrollment.id,
        programId: enrollment.programs?.id ?? null,
        programName: enrollment.programs?.name ?? null,
        studentId: enrollment.students?.id ?? null,
        studentName: [enrollment.students?.first_name, enrollment.students?.last_name]
          .filter(Boolean).join(' ') || null,
        source: 'admin_enrollments_table',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-enrollments'] });
      setIsUnenrollModalOpen(false);
      setEnrollmentToUnenroll(null);
    },
    onError: (err: Error) => {
      alert(`Error unenrolling: ${err.message}`);
    },
  });

  // Update enrollment mutation
  const updateEnrollmentMutation = useMutation({
    mutationFn: async ({ enrollmentId, updates, studentName, programName }: EnrollmentEditVars) => {
      const { error } = await supabase
        .from('enrollments')
        .update(updates)
        .eq('id', enrollmentId);

      if (error) {
        throw new Error(error.message);
      }

      // Also track the event client-side for analytics
      trackEvent({
        eventType: 'admin_enrollment_edit',
        user: currentUser,
        userType: 'admin',
        entityType: 'enrollment',
        entityId: enrollmentId,
        metadata: {
          ...subjectMetadata({ studentName, programName }),
          changes: updates,
          admin_action: true
        },
        success: true
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-enrollments'] });
      setIsEditingEnrollment(false);
      setSelectedEnrollment(null);
      setEditFormData({
        enrollment_start_date: '',
        selected_days: [],
        opt_in_items: []
      });
    },
    onError: async (err: Error, variables) => {
      alert(`Error updating enrollment: ${err.message}`);

      // Also track the error client-side for analytics
      trackEvent({
        eventType: 'admin_enrollment_edit',
        user: currentUser,
        userType: 'admin',
        entityType: 'enrollment',
        entityId: variables.enrollmentId,
        metadata: {
          ...subjectMetadata(variables),
          attempted_changes: variables.updates,
          admin_action: true
        },
        success: false,
        errorMessage: err.message
      });
    },
  });

  // Function to check if an enrollment can be unenrolled
  const canUnenroll = useMemo(() => {
    return (enrollment: EnrollmentWithDetails) => {
      if (!enrollment) {
        return false;
      }

      // Parse enrollment start date consistently using UTC to avoid timezone issues
      if (!enrollment.enrollment_start_date) {
        return false;
      }
      
      const enrollmentStartDate = new Date(
        parseInt(enrollment.enrollment_start_date.split('-')[0]),
        parseInt(enrollment.enrollment_start_date.split('-')[1]) - 1,
        parseInt(enrollment.enrollment_start_date.split('-')[2])
      );
      
      // Get current date in the same timezone format
      const now = new Date();
      const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      if (enrollmentStartDate <= todayLocal) {
        return false;
      }

      // Block when money has moved (paid / partial / processing). Covers the old
      // "any amount paid OR any processing payment" rule via the precomputed status.
      if (hasBlockingPayment(enrollment.payment_status)) {
        return false;
      }

      return true;
    };
  }, []);

  const handleUnenroll = (enrollment: EnrollmentWithDetails) => {
    setEnrollmentToUnenroll(enrollment);
    setIsUnenrollModalOpen(true);
  };

  const confirmUnenroll = () => {
    if (enrollmentToUnenroll) {
      unenrollMutation.mutate(enrollmentToUnenroll);
    }
  };

  // Filter enrollments by search term and program filter
  const matchingEnrollments = enrollments?.filter(
    (enrollment) => {
      // Enhanced search functionality
      const matchesSearch = (() => {
        if (!searchTerm.trim()) return true;
        
        const keywords = searchTerm.toLowerCase().trim().split(/\s+/);
        const searchableFields = [
          enrollment.students?.first_name?.toLowerCase() || '',
          enrollment.students?.last_name?.toLowerCase() || '',
          enrollment.programs?.name?.toLowerCase() || '',
          enrollment.students?.metadata?.school?.toLowerCase() || '',
          enrollment.students?.metadata?.grade?.toLowerCase() || ''
        ];
        
        // Helper function to check if a keyword matches a field, with special handling for grade abbreviations
        const fieldMatchesKeyword = (field: string, keyword: string, fieldIndex: number) => {
          // Special handling for grade field (index 4)
          if (fieldIndex === 4) {
            // Check if keyword is a grade abbreviation like "g2", "g6", etc.
            const gradeMatch = keyword.match(/^g(\d+)$/);
            if (gradeMatch) {
              const gradeNumber = gradeMatch[1];
              // Check if the grade field contains the number
              return field.includes(gradeNumber);
            }
          }
          
          // Default behavior: check if field contains keyword
          return field.includes(keyword);
        };
        
        // For single keyword, check if any field contains it
        if (keywords.length === 1) {
          return searchableFields.some((field, index) => fieldMatchesKeyword(field, keywords[0], index));
        }
        
        // For multiple keywords, each keyword must match a different field
        const matchedFields = new Set<number>();
        
        for (const keyword of keywords) {
          let keywordMatched = false;
          for (let i = 0; i < searchableFields.length; i++) {
            if (!matchedFields.has(i) && fieldMatchesKeyword(searchableFields[i], keyword, i)) {
              matchedFields.add(i);
              keywordMatched = true;
              break;
            }
          }
          if (!keywordMatched) {
            return false; // If any keyword doesn't match, return false
          }
        }
        
        return true; // All keywords matched different fields
      })();
      
      const matchesProgramFilter = !selectedProgramFilter || enrollment.program_id === selectedProgramFilter;
      
      // Skip inactive programs if the filter is set
      const matchesActiveFilter = showInactivePrograms || enrollment.programs.active;
      
      // Skip inactive enrollments if the filter is set
      const matchesEnrollmentActiveFilter = showInactiveEnrollments || enrollment.is_active;
      
      // Skip voided enrollments if the exclude voided filter is set
      const matchesVoidedFilter = !excludeVoided || enrollment.payment_status !== 'voided';
      
      const matchesBrand = matchesBrandFilter(enrollment.programs?.brand_key, brandFilter);

      return matchesSearch && matchesProgramFilter && matchesActiveFilter && matchesEnrollmentActiveFilter && matchesVoidedFilter && matchesBrand;
    }
  );
  // A link shows its one enrollment whatever the filters say; the banner says so.
  const filteredEnrollments = focusedEnrollmentId
    ? enrollments?.filter(enrollment => enrollment.id === focusedEnrollmentId)
    : matchingEnrollments;

  // Function to check if an enrollment can be edited
  const canEditEnrollment = (enrollment: EnrollmentWithDetails) => {
    if (!enrollment) return false;

    // Don't allow editing when money has moved (paid / partial / processing).
    if (hasBlockingPayment(enrollment.payment_status)) {
      return false;
    }

    return true;
  };

  const handleEditEnrollmentClick = (enrollment: EnrollmentWithDetails) => {
    if (!canEditEnrollment(enrollment)) {
      alert('This enrollment cannot be edited. It may have payments or processing payments.');
      return;
    }

    setSelectedEnrollment(enrollment);
    setEditFormData({
      enrollment_start_date: enrollment.enrollment_start_date || '',
      selected_days: selectedDaysOf(enrollment),
      opt_in_items: optInItemsOf(enrollment) ?? []
    });
    setIsEditingEnrollment(true);
  };

  const handleCancelEdit = () => {
    setIsEditingEnrollment(false);
    setSelectedEnrollment(null);
    setEditFormData({
      enrollment_start_date: '',
      selected_days: [],
      opt_in_items: []
    });
  };

  const handleSaveEdit = () => {
    if (!selectedEnrollment) return;

    const updates: Partial<Enrollment> = {};
    
    // Only include fields that have changed
    if (editFormData.enrollment_start_date !== selectedEnrollment.enrollment_start_date) {
      updates.enrollment_start_date = editFormData.enrollment_start_date;
    }
    
    // Metadata is the source of truth, and a jsonb update REPLACES the column -- so start
    // from what is already there. Building this object from scratch silently dropped every
    // other key on save: signed_waiver, level, camp_lunch_selected, and now opt_in_items,
    // which billing reads.
    const md: Record<string, unknown> = {
      ...((selectedEnrollment as unknown as { metadata?: Record<string, unknown> }).metadata ?? {}),
    };
    md.selected_days = editFormData.selected_days || [];

    // The form holds the positive selection directly, so it is written as-is. If the optional
    // selector never loaded, this is the value hydrated from the enrollment, unchanged.
    md.opt_in_items = editFormData.opt_in_items || [];
    delete md.opt_out_items;
    (updates as unknown as { metadata?: Record<string, unknown> }).metadata = md;

    // Only update if there are actual changes
    if (Object.keys(updates).length > 0) {
      updateEnrollmentMutation.mutate({
        enrollmentId: selectedEnrollment.id,
        updates,
        studentName: `${selectedEnrollment.students.first_name} ${selectedEnrollment.students.last_name}`,
        programName: selectedEnrollment.programs.name,
      });
    } else {
      // No changes, just close the modal
      handleCancelEdit();
    }
  };

  const handleProgramCardClick = (programId: string) => {
    setSelectedProgramFilter(programId);
    // One URL write for both, or the second would bring the link back (see useFocusedRecordId).
    if (focusedEnrollmentId) clearFocusedEnrollment(params => params.set('view', 'details'));
    else setViewMode('details');
  };

  const handleClearFilter = () => {
    setSelectedProgramFilter(null);
    setViewMode('summary');
  };


  if (enrollmentsError || breakdownError) {
    return (
      <AdminLayout>
        <div className="mb-4 p-4 bg-red-50 text-red-600 rounded">
          Error loading enrollments
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-white shadow sm:overflow-hidden sm:rounded-lg lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
        <div className="border-b border-gray-200 flex-shrink-0">
          <nav className="-mb-px flex items-center justify-between">
            <div className="flex">
              <button
                className={`whitespace-nowrap py-3 px-4 text-sm font-medium ${
                  viewMode === 'summary'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setViewMode('summary')}
              >
                Summary
              </button>
              <button
                className={`whitespace-nowrap py-3 px-4 text-sm font-medium ${
                  viewMode === 'details'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setViewMode('details')}
              >
                Details
              </button>
            </div>
            {/* Prune button — all screen sizes */}
            <button
              type="button"
              onClick={async () => {
                setBulkError(null);
                setIsBulkModalOpen(true);
                setBulkPreviewLoading(true);
                try {
                  const todayInBusinessTZ = new Intl.DateTimeFormat('en-CA', {
                    timeZone: 'America/Los_Angeles',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }).format(new Date());
                  const { data: endedPrograms, error: programsError } = await supabase
                    .from('programs')
                    .select('id, name')
                    .lt('end_date', todayInBusinessTZ);
                  if (programsError) throw programsError;
                  const programs = (endedPrograms as Array<{ id: string; name: string }>) || [];
                  const programIds = programs.map((p) => p.id);
                  setBulkPreviewProgramsCount(programs.length);
                  if (programIds.length === 0) {
                    setBulkPreviewEnrollmentsCount(0);
                    setBulkProgramBreakdown([]);
                  } else {
                    const { data: enrollmentsForPrograms, error: enrollmentsErr } = await supabase
                      .from('enrollments')
                      .select('id, program_id')
                      .in('program_id', programIds)
                      .eq('is_active', true);
                    if (enrollmentsErr) throw enrollmentsErr;
                    type EnrollmentRow = { id: string; program_id: string };
                    const rows = (enrollmentsForPrograms as EnrollmentRow[]) || [];
                    const countsByProgramId = new Map<string, number>();
                    rows.forEach((r) => countsByProgramId.set(r.program_id, (countsByProgramId.get(r.program_id) || 0) + 1));
                    const breakdown = programs
                      .map((p) => ({
                        programId: p.id,
                        programName: p.name,
                        activeEnrollmentCount: countsByProgramId.get(p.id) || 0,
                      }))
                      .filter((b) => b.activeEnrollmentCount > 0);
                    setBulkProgramBreakdown(breakdown);
                    const total = breakdown.reduce((sum, b) => sum + b.activeEnrollmentCount, 0);
                    setBulkPreviewEnrollmentsCount(total);
                  }
                } catch (err) {
                  const msg = (err as { message?: string }).message || 'Failed to prepare preview.';
                  setBulkError(msg);
                } finally {
                  setBulkPreviewLoading(false);
                }
              }}
              className="mr-3 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors whitespace-nowrap"
            >
              Prune
            </button>
          </nav>
        </div>

        <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0 space-y-2">
          {/* Search row — details has enrollment search, summary has program name filter */}
          {viewMode === 'details' && (
            <div className="flex flex-row items-center space-x-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search enrollments..."
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => {
                    if (focusedEnrollmentId) clearFocusedEnrollment();
                    setSearchTerm(e.target.value);
                  }}
                />
              </div>
              {selectedProgramFilter && (
                <button
                  onClick={handleClearFilter}
                  className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors whitespace-nowrap"
                >
                  Clear Filter
                </button>
              )}
            </div>
          )}
          {viewMode === 'summary' && (
            <div>
              <input
                type="text"
                placeholder="Filter by program name..."
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={summaryNameFilter}
                onChange={(e) => setSummaryNameFilter(e.target.value)}
              />
            </div>
          )}
          {/* Toggles row — visible in both summary and details */}
          <div className="flex items-center gap-3 flex-wrap">
              <BrandFilterSelect
                value={brandFilter}
                onChange={setBrandFilter}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-600 whitespace-nowrap">Inactive pgm</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showInactivePrograms}
                  onClick={() => setShowInactivePrograms(!showInactivePrograms)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${showInactivePrograms ? 'bg-blue-600' : 'bg-gray-200'}`}
                  aria-label="Toggle inactive programs"
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${showInactivePrograms ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-600 whitespace-nowrap">Inactive enroll</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showInactiveEnrollments}
                  onClick={() => setShowInactiveEnrollments(!showInactiveEnrollments)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${showInactiveEnrollments ? 'bg-blue-600' : 'bg-gray-200'}`}
                  aria-label="Toggle inactive enrollments"
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${showInactiveEnrollments ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-600 whitespace-nowrap">Voided</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!excludeVoided}
                  onClick={() => setExcludeVoided(!excludeVoided)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${!excludeVoided ? 'bg-blue-600' : 'bg-gray-200'}`}
                  aria-label="Toggle show include voided"
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${!excludeVoided ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
        </div>

        {/* Bulk Deactivate Modal */}
        <Modal
          isOpen={isBulkModalOpen}
          onClose={() => {
            setIsBulkModalOpen(false);
            setBulkError(null);
            setBulkPreviewProgramsCount(null);
            setBulkPreviewEnrollmentsCount(null);
            setBulkPreviewLoading(false);
            setBulkProgramBreakdown([]);
          }}
          title="Deactivate ended program enrollments"
        >
          <div className="space-y-4">
            {bulkError && (
              <div className="p-3 rounded bg-red-50 text-red-700 text-sm">{bulkError}</div>
            )}
            <p className="text-sm text-gray-700">
              This will set enrollments to Inactive for all programs whose end date is before today (America/Los_Angeles). Existing invoices will not be changed.
            </p>
            {bulkPreviewLoading ? (
              <div className="text-sm text-gray-600">Preparing preview…</div>
            ) : (
              <div className="text-sm text-gray-800 space-y-1">
                <div>Enrollments to deactivate: <span className="font-medium">{bulkPreviewEnrollmentsCount ?? '—'}</span></div>
                {bulkProgramBreakdown.length > 0 && (
                  <div className="mt-2 border-t pt-2">
                    <div className="text-xs text-gray-600 mb-1">Per program:</div>
                    <div className="max-h-48 overflow-auto space-y-1">
                      {bulkProgramBreakdown.map((row) => (
                        <div key={row.programId} className="flex items-center justify-between">
                          <span className="truncate pr-2">{row.programName}</span>
                          <span className="font-medium">{row.activeEnrollmentCount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setIsBulkModalOpen(false);
                  setBulkError(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={bulkPreviewLoading || (bulkPreviewEnrollmentsCount ?? 0) === 0}
                onClick={async () => {
                  setBulkError(null);
                  // Compute today's date in America/Los_Angeles as YYYY-MM-DD
                  const todayInBusinessTZ = new Intl.DateTimeFormat('en-CA', {
                    timeZone: 'America/Los_Angeles',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }).format(new Date());

                  try {
                    const { data: endedPrograms, error: programsError } = await supabase
                      .from('programs')
                      .select('id, name')
                      .lt('end_date', todayInBusinessTZ);
                    if (programsError) throw programsError;

                    const programs = (endedPrograms as Array<{ id: string; name: string }>) || [];
                    const programIds = programs.map((p) => p.id);

                    if (programIds.length === 0) {
                      setIsBulkModalOpen(false);
                      return;
                    }

                    // Capture WHICH rows are about to go inactive, not just how many. The
                    // admin overview derives its "students/programs with enrollment" 24h
                    // deltas from this event trail, and one bulk event covering hundreds of
                    // rows carries no row identity of its own -- without these ids a bulk
                    // deactivation would silently count as zero students affected.
                    const { data: affectedRows } = await supabase
                      .from('enrollments')
                      .select('id, student_id, program_id')
                      .in('program_id', programIds)
                      .eq('is_active', true);
                    const affected = (affectedRows as Array<{ id: string; student_id: string; program_id: string }>) || [];

                    const { error: updateError } = await supabase
                      .from('enrollments')
                      .update({ is_active: false })
                      .in('program_id', programIds)
                      .eq('is_active', true);
                    if (updateError) throw updateError;

                    trackEvent({
                      eventType: 'admin_bulk_deactivate_enrollments',
                      user: currentUser,
                      entityType: 'enrollment',
                      success: true,
                      metadata: {
                        program_count: programs.length,
                        enrollment_count: bulkPreviewEnrollmentsCount,
                        program_names: programs.map((p) => p.name).join(', '),
                        student_ids: [...new Set(affected.map((e) => e.student_id))],
                        program_ids: [...new Set(affected.map((e) => e.program_id))],
                      },
                    });

                    setIsBulkModalOpen(false);
                    setBulkPreviewProgramsCount(null);
                    setBulkPreviewEnrollmentsCount(null);
                    setBulkProgramBreakdown([]);
                    queryClient.invalidateQueries({ queryKey: ['admin-enrollments'] });
                    alert('Ended program enrollments have been deactivated.');
                  } catch (err) {
                    const msg = (err as { message?: string }).message || 'Bulk deactivation failed.';
                    setBulkError(msg);
                    trackEvent({
                      eventType: 'admin_bulk_deactivate_enrollments',
                      user: currentUser,
                      entityType: 'enrollment',
                      success: false,
                      errorMessage: msg,
                    });
                  }
                }}
                className={`px-4 py-2 rounded-md text-white transition-colors ${
                  bulkPreviewLoading || (bulkPreviewEnrollmentsCount ?? 0) === 0
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-orange-600 hover:bg-orange-700'
                }`}
              >
                Confirm Deactivate
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={isEditingEnrollment}
          onClose={handleCancelEdit}
          title="Edit Enrollment"
        >
          <div className="space-y-6">
            <div className="text-sm text-gray-600">
              {`Editing enrollment for ${selectedEnrollment?.students.first_name} ${selectedEnrollment?.students.last_name} in ${selectedEnrollment?.programs.name}`}
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enrollment Start Date
              </label>
              <input
                type="date"
                value={editFormData.enrollment_start_date}
                onChange={(e) => setEditFormData(prev => ({
                  ...prev,
                  enrollment_start_date: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min={selectedEnrollment?.programs.start_date}
                max={selectedEnrollment?.programs.end_date}
              />
              <p className="text-xs text-gray-500 mt-1">
                Program runs from {selectedEnrollment?.programs.start_date} to {selectedEnrollment?.programs.end_date}
              </p>
            </div>

            {/* Selected Days for per_day programs */}
            {selectedEnrollment?.programs.billing_type === 'per_day' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selected Days
                </label>
                <DaySelector
                  startDate={selectedEnrollment.programs.start_date}
                  endDate={selectedEnrollment.programs.end_date}
                  selectedDays={editFormData.selected_days}
                  onChange={(selectedDays) => setEditFormData(prev => ({
                    ...prev,
                    selected_days: selectedDays
                  }))}
                  minSelections={1}
                  scheduleCron={selectedEnrollment.programs.schedule_cron}
                  // Nothing new is offered until the calendar loads; a day already selected
                  // can still be removed.
                  isSelectableDate={perDayProgramCalendar ? sessionDayPredicate(perDayProgramCalendar) : () => false}
                />
              </div>
            )}

            {/* Flex monthly: edit the next month's day plan */}
            {selectedEnrollment?.programs.billing_type === 'flex_monthly' && (
              <div>
                <AdminFlexPlanSection
                  enrollmentId={selectedEnrollment.id}
                  programId={selectedEnrollment.programs.id}
                  brandKey={selectedEnrollment.programs.brand_key}
                  programSchedule={selectedEnrollment.programs.schedule_cron}
                  /* The modal's live selection, not the saved metadata, so the per-day rate
                     tracks the checkboxes the admin is toggling right above this. */
                  optInItems={editFormData.opt_in_items}
                />
              </div>
            )}

            {/* Optional Items */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Optional Items
              </label>
              <OptionalItemsSelector
                programId={selectedEnrollment?.programs.id || ''}
                selectedOptInItems={editFormData.opt_in_items}
                onChange={(optInItems) => setEditFormData(prev => ({
                  ...prev,
                  opt_in_items: optInItems
                }))}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                disabled={updateEnrollmentMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={updateEnrollmentMutation.isPending}
              >
                {updateEnrollmentMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </Modal>


        {(viewMode === 'summary' ? breakdownLoading : enrollmentsLoading) ? (
          <div className="flex items-center justify-center py-12">
            <svg className="animate-spin h-4 w-4 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
            <span className="text-gray-600">Loading enrollments...</span>
          </div>
        ) : (
          <>
        {viewMode === 'summary' && (
          // Summary View - Programs as rows, Stats as columns
          <div className="flex flex-col lg:flex-1 lg:min-h-0">
            <div className="sticky top-14 z-30 lg:static lg:z-auto flex-shrink-0">
              <ProgramTypeScrollNav
                types={summaryTypes}
                activeType={summaryTypeNav.activeType}
                onSelect={summaryTypeNav.scrollToType}
              />
            </div>
            <div className="overflow-x-auto sm:overflow-y-auto sm:max-h-[calc(100vh-12rem)] lg:max-h-none lg:overflow-y-auto lg:flex-1 lg:min-h-0 -mx-2 sm:mx-0">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="sticky top-0 z-20 bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="sticky left-0 z-30 px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-r border-gray-200 shadow-sm">
                      Program
                    </th>
                    <th className="px-2 py-3 sm:px-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Total
                    </th>
                    <th className="px-2 py-3 sm:px-3 text-center text-xs font-medium text-orange-600 uppercase tracking-wider bg-gray-50">
                      Active
                    </th>
                    <th className="px-2 py-3 sm:px-3 text-center text-xs font-medium text-red-600 uppercase tracking-wider bg-gray-50">
                      Inactive
                    </th>
                    <th className="px-2 py-3 sm:px-3 text-center text-xs font-medium text-blue-600 uppercase tracking-wider bg-gray-50">
                      Pending
                    </th>
                    <th className="px-2 py-3 sm:px-3 text-center text-xs font-medium text-purple-600 uppercase tracking-wider bg-gray-50">
                      Processing
                    </th>
                    <th className="px-2 py-3 sm:px-3 text-center text-xs font-medium text-yellow-600 uppercase tracking-wider bg-gray-50">
                      Partial
                    </th>
                    <th className="px-2 py-3 sm:px-3 text-center text-xs font-medium text-green-600 uppercase tracking-wider bg-gray-50">
                      Paid
                    </th>
                    <th className="px-2 py-3 sm:px-3 text-center text-xs font-medium text-red-600 uppercase tracking-wider bg-gray-50">
                      Overdue
                    </th>
                    {!excludeVoided && (
                      <th className="px-2 py-3 sm:px-3 text-center text-xs font-medium text-red-600 uppercase tracking-wider bg-gray-50">
                        Voided
                      </th>
                    )}
                    {allSchools.map((school) => (
                      <th key={school} className="px-2 py-3 sm:px-3 text-center text-xs font-medium text-indigo-600 uppercase tracking-wider bg-gray-50">
                        {shortenSchoolName(school)}
                      </th>
                    ))}
                    {allGrades.map((grade) => (
                      <th key={grade} className="px-2 py-3 sm:px-3 text-center text-xs font-medium text-teal-600 uppercase tracking-wider bg-gray-50">
                        {formatGradeDisplay(grade)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    const filteredPivotData = summaryNameFilter
                      ? pivotData.filter(row => row.program.name.toLowerCase().includes(summaryNameFilter.toLowerCase()))
                      : pivotData;
                    const grouped = new Map<string, Array<(typeof pivotData)[number]>>();
                    filteredPivotData.forEach((row) => {
                      const t = (row.program.type || 'other') as ProgramType;
                      if (!grouped.has(t)) grouped.set(t, []);
                      grouped.get(t)!.push(row);
                    });
                    const sortedTypes = Array.from(grouped.keys()).sort(compareProgramTypes);
                    const totalColumns = 4 + (excludeVoided ? 5 : 6) + allSchools.length + allGrades.length;
                    return sortedTypes.flatMap((typeKey) => {
                      const rows: JSX.Element[] = [];
                      rows.push(
                        <tr key={`sep-${typeKey}`} ref={summaryTypeNav.setSectionRef(typeKey)} className="scroll-mt-28 sm:scroll-mt-12" style={{ backgroundColor: getProgramTypeSectionColor(typeKey) }}>
                          <td className="sticky left-0 z-10 px-3 py-2 sm:px-6 sm:py-3 text-xs font-semibold tracking-wide border-r border-gray-300 shadow-sm text-white" style={{ backgroundColor: getProgramTypeSectionColor(typeKey) }}>
                            <span className="mr-1.5">{getProgramTypeIcon(typeKey)}</span>
                            {getProgramTypeDisplayName(typeKey as ProgramType)}
                          </td>
                          <td colSpan={totalColumns - 1} className="px-2 py-2 sm:px-3 sm:py-3" />
                        </tr>
                      );
                      grouped.get(typeKey)!.forEach((row) => {
                        rows.push(
                          <tr
                            key={row.program.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleProgramCardClick(row.program.id)}
                          >
                            <td className="sticky left-0 z-10 px-3 py-3 sm:px-6 sm:py-4 bg-white border-r border-gray-200 shadow-sm min-w-[14rem] max-w-[20rem]">
                              <div>
                                <div className="text-sm font-medium text-gray-900 break-words">{row.program.name} <AdminBrandTag brand={row.program.brand_key} /></div>
                                <div className="text-xs text-gray-500">
                                  {getProgramTypeDisplayName(row.program.type)} • ${row.program.price} • {row.program.active ? (
                                    <span className="text-green-600">Active</span>
                                  ) : (
                                    <span className="text-gray-500">Inactive</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {formatDateDisplay(row.program.start_date || '')} - {formatDateDisplay(row.program.end_date || '')}
                                </div>
                              </div>
                            </td>
                            <td className="px-2 py-3 sm:px-3 sm:py-4 whitespace-nowrap text-center">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {row.total}
                              </span>
                            </td>
                            <td className="px-2 py-3 sm:px-3 sm:py-4 whitespace-nowrap text-center bg-orange-50 bg-opacity-30">
                              {row.activeEnrollments ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  {row.activeEnrollments}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="px-2 py-3 sm:px-3 sm:py-4 whitespace-nowrap text-center bg-red-50 bg-opacity-30">
                              {row.inactiveEnrollments ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  {row.inactiveEnrollments}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="px-2 py-3 sm:px-3 sm:py-4 whitespace-nowrap text-center bg-blue-50 bg-opacity-30 border-l-2 border-gray-300">
                              {row.paymentStatusCounts.get('pending') ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {row.paymentStatusCounts.get('pending')}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="px-2 py-3 sm:px-3 sm:py-4 whitespace-nowrap text-center bg-blue-50 bg-opacity-30">
                              {row.paymentStatusCounts.get('payment_processing') ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  {row.paymentStatusCounts.get('payment_processing')}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="px-2 py-3 sm:px-3 sm:py-4 whitespace-nowrap text-center bg-blue-50 bg-opacity-30">
                              {row.paymentStatusCounts.get('partial') ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {row.paymentStatusCounts.get('partial')}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="px-2 py-3 sm:px-3 sm:py-4 whitespace-nowrap text-center bg-blue-50 bg-opacity-30">
                              {row.paymentStatusCounts.get('paid') ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {row.paymentStatusCounts.get('paid')}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="px-2 py-3 sm:px-3 sm:py-4 whitespace-nowrap text-center bg-blue-50 bg-opacity-30">
                              {row.paymentStatusCounts.get('overdue') ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  {row.paymentStatusCounts.get('overdue')}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            {!excludeVoided && (
                              <td className="px-2 py-3 sm:px-3 sm:py-4 whitespace-nowrap text-center bg-blue-50 bg-opacity-30">
                                {row.paymentStatusCounts.get('voided') ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    {row.paymentStatusCounts.get('voided')}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                            )}
                            {allSchools.map((school, index) => (
                              <td key={school} className={`px-2 py-3 sm:px-3 sm:py-4 whitespace-nowrap text-center bg-indigo-50 bg-opacity-30 ${index === 0 ? 'border-l-2 border-gray-300' : ''}`}>
                                {row.schoolCounts.get(school) ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                    {row.schoolCounts.get(school)}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                            ))}
                            {allGrades.map((grade, index) => (
                              <td key={grade} className={`px-2 py-3 sm:px-3 sm:py-4 whitespace-nowrap text-center bg-teal-50 bg-opacity-30 ${index === 0 ? 'border-l-2 border-gray-300' : ''}`}>
                                {row.gradeCounts.get(grade) ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                                    {row.gradeCounts.get(grade)}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      });
                      return rows;
                    });
                  })()}
                </tbody>
              </table>
            </div>
            
            {!enrollmentsLoading && pivotData.length > 0 && summaryNameFilter && pivotData.filter(row => row.program.name.toLowerCase().includes(summaryNameFilter.toLowerCase())).length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No programs match &quot;{summaryNameFilter}&quot;</p>
              </div>
            )}
            {!enrollmentsLoading && pivotData.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No programs found</p>
              </div>
            )}
          </div>
        )}

        {viewMode === 'details' && (
          // Details View - Enrollment Table
          <div className="flex flex-col lg:flex-1 lg:min-h-0">
            {focusedEnrollmentId && (
              <FocusedRecordBanner
                noun="enrollment"
                loading={enrollmentsLoading}
                found={(filteredEnrollments?.length ?? 0) > 0}
                onShowAll={clearFocusedEnrollment}
              />
            )}
            {/* Summary Header */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
              {selectedProgramFilter ? (
                // Program-specific summary
                (() => {
                  const selectedProgram = programSummaries.find(s => s.program.id === selectedProgramFilter);
                  if (!selectedProgram) return null;
                  
                  return (
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-3 sm:space-y-0">
                      <div className="flex-1">
                        <h2 className="text-lg font-semibold text-gray-900">{selectedProgram.program.name}</h2>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                          <span>Type: <span className="font-medium">{getProgramTypeDisplayName(selectedProgram.program.type)}</span></span>
                          <span>•</span>
                          <span>Price: <span className="font-medium">${selectedProgram.program.price}</span></span>
                          <span>•</span>
                          <span>{formatDateDisplay(selectedProgram.program.start_date || '')} - {formatDateDisplay(selectedProgram.program.end_date || '')}</span>
                          <span>•</span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            selectedProgram.program.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {selectedProgram.program.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Total: {selectedProgram.totalEnrollments}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Pending: {selectedProgram.pendingEnrollments}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Processing: {selectedProgram.processingEnrollments}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Paid: {selectedProgram.paidEnrollments}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Partial: {selectedProgram.partialEnrollments}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Overdue: {selectedProgram.overdueEnrollments}
                        </span>
                        {!excludeVoided && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Voided: {selectedProgram.voidedEnrollments}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()
              ) : (
                // Overall summary
                (() => {
                  const totalEnrollments = filteredEnrollments?.length || 0;
                  const activeEnrollments = filteredEnrollments?.filter(e => e.is_active).length || 0;
                  const inactiveEnrollments = totalEnrollments - activeEnrollments;
                  const totalPending = programSummaries.reduce((sum, s) => sum + s.pendingEnrollments, 0);
                  const totalProcessing = programSummaries.reduce((sum, s) => sum + s.processingEnrollments, 0);
                  const totalPaid = programSummaries.reduce((sum, s) => sum + s.paidEnrollments, 0);
                  const totalPartial = programSummaries.reduce((sum, s) => sum + s.partialEnrollments, 0);
                  const totalOverdue = programSummaries.reduce((sum, s) => sum + s.overdueEnrollments, 0);
                  const totalVoided = programSummaries.reduce((sum, s) => sum + s.voidedEnrollments, 0);
                  return (
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-3 sm:space-y-0">
                      <div className="flex-1">
                        <h2 className="text-lg font-semibold text-gray-900">
                          {brandFilter === 'all' ? 'All Enrollments' : `All ${BRAND_PROFILES[brandFilter].shortName} Enrollments`}
                        </h2>
                        <div className="mt-2 text-sm text-gray-600">
                          Showing {filteredEnrollments?.length || 0} of {totalEnrollments} enrollments
                          {searchTerm && ` matching "${searchTerm}"`}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Total: {totalEnrollments}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active: {activeEnrollments}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Inactive: {inactiveEnrollments}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Pending: {totalPending}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Processing: {totalProcessing}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Paid: {totalPaid}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Partial: {totalPartial}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Overdue: {totalOverdue}
                        </span>
                        {!excludeVoided && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Voided: {totalVoided}
                          </span>
                        )}
                        </div>
                    </div>
                  );
                })()
              )}
            </div>

            <div className="overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0">
                  <table className="min-w-full divide-y divide-gray-200 hidden lg:table">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                        >
                          Student
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                        >
                          Parent
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                        >
                          School / Grade
                        </th>

                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                        >
                          Status
                        </th>

                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                        >
                          Program
                        </th>
                         <th
                           scope="col"
                           className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                         >
                           Dates
                         </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                        >
                          Opt-Out Items
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                        >
                          Created
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                        >
                          Payment
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredEnrollments?.map((enrollment) => {
                        // Latest-invoice summary comes inline from the lightweight payload.
                        const invoice = enrollment.latest_invoice ?? null;
                        const amountDue = invoice ? invoice.total_amount - invoice.amount_paid : 0;
                        // Only show payment status for active enrollments
                        const paymentStatus = enrollment.is_active ? (enrollment.payment_status ?? 'no_invoice') : 'inactive';
                        
                        return (
                          <React.Fragment key={enrollment.id}>
                          <tr
                            className={selectedEnrollmentId === enrollment.id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'}
                            onClick={() => setSelectedEnrollmentId(selectedEnrollmentId === enrollment.id ? null : enrollment.id)}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {enrollment.students?.first_name} {enrollment.students?.last_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                Age: {calculateAge(enrollment.students?.date_of_birth)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {enrollment.students?.student_parents?.length ? (
                                <div className="space-y-1.5">
                                  {enrollment.students.student_parents.map(sp => (
                                    <div key={sp.parent_id} className="text-xs text-gray-600 space-y-0.5">
                                      <div className="font-medium text-gray-800">
                                        {[sp.parents.first_name, sp.parents.last_name].filter(Boolean).join(' ')}
                                        {sp.is_primary && <span className="ml-1 text-blue-600">(primary)</span>}
                                      </div>
                                      <div>{sp.parents.email}</div>
                                      {sp.parents.phone && <div>{sp.parents.phone}</div>}
                                    </div>
                                  ))}
                                </div>
                              ) : <span className="text-xs text-gray-400">—</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {(() => {
                                const school = displaySchool(enrollment.students?.metadata?.school);
                                const grade = displayGrade(enrollment.students?.metadata?.grade);
                                if (!school && !grade) return <span className="text-xs text-gray-400">—</span>;
                                return (
                                  <div className="text-sm text-gray-900 space-y-0.5">
                                    {school && <div>{school}</div>}
                                    {grade && <div className="text-xs text-gray-500">{grade}</div>}
                                  </div>
                                );
                              })()}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col space-y-1">
                                <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                                  paymentStatus === 'inactive' ? 'bg-gray-100 text-gray-800' :
                                  paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                                  paymentStatus === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                  paymentStatus === 'pending' ? 'bg-blue-100 text-blue-800' :
                                  paymentStatus === 'payment_processing' ? 'bg-purple-100 text-purple-800' :
                                  paymentStatus === 'overdue' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {paymentStatus === 'inactive' ? 'INACTIVE' : paymentStatus.replace(/_/g, ' ').toUpperCase()}
                                </span>
                                {paymentStatus !== 'inactive' && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    enrollment.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {enrollment.is_active ? 'ACTIVE' : 'INACTIVE'}
                                  </span>
                                )}
                              </div>
                            </td>

                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">{(() => {
                                const baseName = enrollment.programs?.name || '';
                                const md = (enrollment as unknown as { metadata?: Record<string, unknown> }).metadata || {};
                                const lvlUnknown = (md as Record<string, unknown>).level;
                                const lvl = typeof lvlUnknown === 'string' && lvlUnknown.trim() ? lvlUnknown : '';
                                return lvl ? `${baseName} (${lvl})` : baseName;
                              })()} <AdminBrandTag brand={enrollment.programs?.brand_key} /></div>
                              <div className="text-xs text-gray-500">
                                {getProgramTypeDisplayName(enrollment.programs?.type)} • ${enrollment.programs?.price}
                              </div>
                            </td>
                           <td className="px-6 py-4 whitespace-nowrap">
                             {enrollment.programs?.billing_type === 'per_day' ? (
                               selectedDaysOf(enrollment).length > 0 ? (
                                 <div className="text-xs text-gray-700">
                                   <span className="font-medium">Selected Dates ({selectedDaysOf(enrollment).length}):</span>
                                   <div className="flex flex-wrap gap-1 mt-1">
                                     {selectedDaysOf(enrollment).slice(0, 3).map((date: string, idx: number) => (
                                       <span
                                         key={idx}
                                         className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                       >
                                         {formatDateDisplay(date)}
                                       </span>
                                     ))}
                                     {selectedDaysOf(enrollment).length > 3 && (
                                       <span className="text-xs text-gray-500">+{selectedDaysOf(enrollment).length - 3} more</span>
                                     )}
                                   </div>
                                 </div>
                               ) : (
                                 <span className="text-xs text-gray-500">No dates selected</span>
                               )
                             ) : (
                               <div className="text-xs text-gray-700">
                                 <div>Start: {formatDateDisplay(enrollment.enrollment_start_date || '')}</div>
                                 <div>End: {formatDateDisplay(enrollment.enrollment_end_date || '')}</div>
                               </div>
                             )}
                           </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {(optInItemsOf(enrollment) ?? []).length > 0 ? (
                                <OptInNames enrollmentId={enrollment.id} optInIds={optInItemsOf(enrollment) ?? []} />
                              ) : (
                                <span className="text-xs text-gray-500">None</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {formatDateTimeInBusinessTZ(enrollment.created_at)}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {(() => {
                                  const ebp = (enrollment as unknown as { enrolled_by_parent?: { first_name?: string; last_name?: string; email?: string } }).enrolled_by_parent
                                  if (!ebp) return '—'
                                  const name = [ebp.first_name || '', ebp.last_name || ''].filter(Boolean).join(' ').trim()
                                  return `by ${name || ebp.email || '—'}`
                                })()}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                Amount Due: ${amountDue.toFixed(2)}
                              </div>
                              {invoice && (
                                <div className="text-xs text-gray-500">
                                  Invoice: {invoice.status} / ${invoice.amount_paid.toFixed(2)} of ${invoice.total_amount.toFixed(2)}
                                </div>
                              )}
                            </td>
                          </tr>
                          {selectedEnrollmentId === enrollment.id && (
                            <tr key={`${enrollment.id}-actions`} className="bg-blue-50">
                              {/* The row spans a table far wider than the viewport, so the detail
                                  content would stretch with it and force horizontal scrolling to
                                  read a card. Cap it at a readable measure and stick it to the
                                  left edge of the scroll container so it stays put while the
                                  columns above scroll. */}
                              <td colSpan={9} className="p-0 border-t border-blue-100">
                                <div className="sticky left-0 w-[min(100%,52rem)] px-6 py-2 xl:w-[min(100%,64rem)]">
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); copyEnrollmentId(enrollment.id); }}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                                    title="Copy enrollment ID to clipboard"
                                  >
                                    <Copy className="h-3 w-3 mr-1" />
                                    Copy ID
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleEditEnrollmentClick(enrollment); }}
                                    disabled={!canEditEnrollment(enrollment)}
                                    className={`inline-flex items-center px-2 py-1 text-xs font-medium ${
                                      canEditEnrollment(enrollment)
                                        ? 'text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors'
                                        : 'text-gray-400 bg-gray-50 border border-gray-200 rounded-md cursor-not-allowed'
                                    }`}
                                    title={
                                      canEditEnrollment(enrollment)
                                        ? 'Edit enrollment'
                                        : 'Cannot edit: enrollment has payments or processing payments'
                                    }
                                  >
                                    <Edit className="h-3 w-3 mr-1" />
                                    Edit
                                  </button>
                                  {canUnenroll(enrollment) ? (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleUnenroll(enrollment); }}
                                      disabled={unenrollMutation.isPending}
                                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors disabled:opacity-50"
                                      title="Unenroll from this program"
                                    >
                                      <UserMinus className="h-3 w-3 mr-1" />
                                      {unenrollMutation.isPending ? 'Processing...' : 'Unenroll'}
                                    </button>
                                  ) : (
                                    enrollment.is_active ? (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); toggleEnrollmentStatus.mutate({ enrollmentId: enrollment.id, isActive: false, studentName: `${enrollment.students.first_name} ${enrollment.students.last_name}`, programName: enrollment.programs.name }); }}
                                        disabled={toggleEnrollmentStatus.isPending}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 hover:text-orange-900 transition-colors disabled:opacity-50"
                                        title="Deactivate enrollment"
                                      >
                                        <PowerOff className="h-3 w-3 mr-1" />
                                        {toggleEnrollmentStatus.isPending ? 'Processing...' : 'Deactivate'}
                                      </button>
                                    ) : (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); toggleEnrollmentStatus.mutate({ enrollmentId: enrollment.id, isActive: true, studentName: `${enrollment.students.first_name} ${enrollment.students.last_name}`, programName: enrollment.programs.name }); }}
                                        disabled={toggleEnrollmentStatus.isPending}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors disabled:opacity-50"
                                        title="Activate enrollment"
                                      >
                                        <PowerOff className="h-3 w-3 mr-1" />
                                        {toggleEnrollmentStatus.isPending ? 'Processing...' : 'Activate'}
                                      </button>
                                    )
                                  )}
                                </div>
                                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                                  <EnrollmentDetailPanel enrollment={enrollment} />
                                </div>
                                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                                  <EnrollmentInvoiceHistory enrollmentId={enrollment.id} />
                                </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Mobile Card View */}
                  <div className="lg:hidden">
                    {filteredEnrollments?.map((enrollment) => {
                      // Latest-invoice summary comes inline from the lightweight payload.
                      const invoice = enrollment.latest_invoice ?? null;
                      const amountDue = invoice ? invoice.total_amount - invoice.amount_paid : 0;
                      // Only show payment status for active enrollments
                      const paymentStatus = enrollment.is_active ? (enrollment.payment_status ?? 'no_invoice') : 'inactive';

                      return (
                        <div key={enrollment.id} className={`border-b border-gray-200 p-3 cursor-pointer ${selectedEnrollmentId === enrollment.id ? 'bg-blue-50' : ''}`} onClick={() => setSelectedEnrollmentId(selectedEnrollmentId === enrollment.id ? null : enrollment.id)}>
                          <div className="flex items-start mb-2">
                            <div className="flex-1">
                              <div className="text-base font-bold text-gray-900">
                                {enrollment.students?.first_name} {enrollment.students?.last_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                Age: {calculateAge(enrollment.students?.date_of_birth)}
                              </div>
                              <div className="text-sm text-gray-800">
                                {(() => {
                                  const baseName = enrollment.programs?.name || '';
                                  const md = (enrollment as unknown as { metadata?: Record<string, unknown> }).metadata || {};
                                  const lvlUnknown = (md as Record<string, unknown>).level;
                                  const lvl = typeof lvlUnknown === 'string' && lvlUnknown.trim() ? lvlUnknown : '';
                                  return lvl ? `${baseName} (${lvl})` : baseName;
                                })()}
                                {' '}<AdminBrandTag brand={enrollment.programs?.brand_key} />
                                <span className="text-xs text-gray-500 ml-2">
                                  {getProgramTypeDisplayName(enrollment.programs?.type)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="text-sm space-y-1">
                            <div className="flex flex-col space-y-1">
                              <div className="flex items-center">
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                  paymentStatus === 'inactive' ? 'bg-gray-100 text-gray-800' :
                                  paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                                  paymentStatus === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                  paymentStatus === 'pending' ? 'bg-blue-100 text-blue-800' :
                                  paymentStatus === 'payment_processing' ? 'bg-purple-100 text-purple-800' :
                                  paymentStatus === 'overdue' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {paymentStatus === 'inactive' ? 'INACTIVE' : paymentStatus.replace(/_/g, ' ')}
                                </span>
                                {paymentStatus !== 'inactive' && (
                                  <>
                                    <span className="mx-1.5 text-gray-300">|</span>
                                    <span>Due: <span className="font-medium">${amountDue.toFixed(2)}</span></span>
                                  </>
                                )}
                              </div>
                              {paymentStatus !== 'inactive' && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  enrollment.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {enrollment.is_active ? 'ACTIVE' : 'INACTIVE'}
                                </span>
                              )}
                            </div>

                            <div className="text-xs text-gray-600">
                              {(() => {
                                const school = displaySchool(enrollment.students?.metadata?.school);
                                const grade = displayGrade(enrollment.students?.metadata?.grade);
                                if (!school && !grade) return null;
                                return (
                                  <div>
                                    {[school, grade].filter(Boolean).join(' · ')}
                                  </div>
                                );
                              })()}
                              {enrollment.students?.student_parents?.map(sp => {
                                const name = [sp.parents.first_name, sp.parents.last_name].filter(Boolean).join(' ');
                                return (
                                  <div key={sp.parent_id} className="mt-0.5">
                                    <span className="text-gray-800 font-medium">{name}</span>
                                    {' · '}<span>{sp.parents.email}</span>
                                    {sp.parents.phone && <span>{' · '}{sp.parents.phone}</span>}
                                  </div>
                                );
                              })}
                              <div>Created: {formatDateTimeInBusinessTZ(enrollment.created_at)} {(() => {
                                const ebp = (enrollment as unknown as { enrolled_by_parent?: { first_name?: string; last_name?: string; email?: string } }).enrolled_by_parent
                                if (!ebp) return ''
                                const name = [ebp.first_name || '', ebp.last_name || ''].filter(Boolean).join(' ').trim()
                                return `by ${name || ebp.email || ''}`
                              })()}</div>
                            <div className="mt-1">
                              {enrollment.programs?.billing_type === 'per_day' ? (
                                selectedDaysOf(enrollment).length > 0 ? (
                                  <div>
                                    <span className="text-gray-600">Selected Dates ({selectedDaysOf(enrollment).length}): </span>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {selectedDaysOf(enrollment).slice(0, 3).map((date: string, idx: number) => (
                                        <span
                                          key={idx}
                                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                        >
                                          {formatDateDisplay(date)}
                                        </span>
                                      ))}
                                      {selectedDaysOf(enrollment).length > 3 && (
                                        <span className="text-[10px] text-gray-500">+{selectedDaysOf(enrollment).length - 3} more</span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-gray-500">No dates selected</span>
                                )
                              ) : (
                                <span>
                                  {formatDateDisplay(enrollment.enrollment_start_date || '')} - {formatDateDisplay(enrollment.enrollment_end_date || '')}
                                </span>
                              )}
                            </div>
                              <div className="mt-1">
                                <span className="text-gray-600">Opt-Out Items: </span>
                                {(optInItemsOf(enrollment) ?? []).length > 0 ? (
                                  <span className="text-gray-800">{(optInItemsOf(enrollment) ?? []).length}</span>
                                ) : (
                                  <span className="text-gray-500">None</span>
                                )}
                              </div>
                            </div>
                          </div>
                          {selectedEnrollmentId === enrollment.id && (
                            <>
                            <div className="mt-2 pt-2 border-t border-blue-100 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => copyEnrollmentId(enrollment.id)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                                title="Copy enrollment ID to clipboard"
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                Copy ID
                              </button>
                              <button
                                onClick={() => handleEditEnrollmentClick(enrollment)}
                                disabled={!canEditEnrollment(enrollment)}
                                className={`inline-flex items-center px-2 py-1 text-xs font-medium ${
                                  canEditEnrollment(enrollment)
                                    ? 'text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors'
                                    : 'text-gray-400 bg-gray-50 border border-gray-200 rounded-md cursor-not-allowed'
                                }`}
                                title={
                                  canEditEnrollment(enrollment)
                                    ? 'Edit enrollment'
                                    : 'Cannot edit: enrollment has payments or processing payments'
                                }
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Edit
                              </button>
                              {canUnenroll(enrollment) ? (
                                <button
                                  onClick={() => handleUnenroll(enrollment)}
                                  disabled={unenrollMutation.isPending}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors disabled:opacity-50"
                                  title="Unenroll from this program"
                                >
                                  <UserMinus className="h-3 w-3 mr-1" />
                                  {unenrollMutation.isPending ? 'Processing...' : 'Unenroll'}
                                </button>
                              ) : (
                                enrollment.is_active ? (
                                  <button
                                    onClick={() => toggleEnrollmentStatus.mutate({
                                      enrollmentId: enrollment.id,
                                      isActive: false,
                                      studentName: `${enrollment.students.first_name} ${enrollment.students.last_name}`,
                                      programName: enrollment.programs.name,
                                    })}
                                    disabled={toggleEnrollmentStatus.isPending}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 hover:text-orange-900 transition-colors disabled:opacity-50"
                                    title="Deactivate enrollment"
                                  >
                                    <PowerOff className="h-3 w-3 mr-1" />
                                    {toggleEnrollmentStatus.isPending ? 'Processing...' : 'Deactivate'}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => toggleEnrollmentStatus.mutate({
                                      enrollmentId: enrollment.id,
                                      isActive: true,
                                      studentName: `${enrollment.students.first_name} ${enrollment.students.last_name}`,
                                      programName: enrollment.programs.name,
                                    })}
                                    disabled={toggleEnrollmentStatus.isPending}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors disabled:opacity-50"
                                    title="Activate enrollment"
                                  >
                                    <PowerOff className="h-3 w-3 mr-1" />
                                    {toggleEnrollmentStatus.isPending ? 'Processing...' : 'Activate'}
                                  </button>
                                )
                              )}
                            </div>
                            <div className="mt-2 pt-2 border-t border-blue-100" onClick={(e) => e.stopPropagation()}>
                              <EnrollmentDetailPanel enrollment={enrollment} />
                            </div>
                            <div className="mt-2 pt-2 border-t border-blue-100" onClick={(e) => e.stopPropagation()}>
                              <EnrollmentInvoiceHistory enrollmentId={enrollment.id} />
                            </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Unenroll Confirmation Modal */}
      <Modal
        isOpen={isUnenrollModalOpen}
        onClose={() => {
          setIsUnenrollModalOpen(false);
          setEnrollmentToUnenroll(null);
        }}
        title="Confirm Unenrollment"
      >
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to unenroll <strong>{enrollmentToUnenroll?.students?.first_name} {enrollmentToUnenroll?.students?.last_name}</strong> from <strong>{truncateProgramName(enrollmentToUnenroll?.programs?.name || '')}</strong> ({getProgramTypeDisplayName(enrollmentToUnenroll?.programs?.type || 'other')})? This marks the enrollment inactive and sets its end date to today. Invoices and payment history are kept, and the enrollment can be reactivated later.
          </p>
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => {
                setIsUnenrollModalOpen(false);
                setEnrollmentToUnenroll(null);
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmUnenroll}
              disabled={unenrollMutation.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {unenrollMutation.isPending ? 'Processing...' : 'Confirm Unenroll'}
            </button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
} 

// Lazy-loaded full invoice history for a single enrollment. Mounted only when a
// Details row is expanded, so the list payload stays light (it carries just a
// latest-invoice summary). Fetched via the enrollment_invoices RPC.
function EnrollmentInvoiceHistory({ enrollmentId }: { enrollmentId: string }) {
  const { data: invoices, isLoading, error } = useQuery<Invoice[]>({
    queryKey: ['enrollment-invoices', enrollmentId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('enrollment_invoices', {
        p_enrollment_id: enrollmentId,
      });
      if (error) throw error;
      return (Array.isArray(data) ? data : []) as Invoice[];
    },
  });

  if (isLoading) {
    return <div className="text-xs text-gray-500">Loading invoice history…</div>;
  }
  if (error) {
    return <div className="text-xs text-red-600">Failed to load invoice history</div>;
  }
  if (!invoices || invoices.length === 0) {
    return <div className="text-xs text-gray-500">No invoices</div>;
  }

  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
        Invoice History ({invoices.length})
      </div>
      <div className="space-y-1.5">
        {invoices.map((inv) => {
          const due = inv.total_amount - inv.amount_paid;
          return (
            <div key={inv.id} className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-gray-900">
                  {formatDateDisplay(inv.period_start || '')}
                  {inv.period_end ? ` – ${formatDateDisplay(inv.period_end)}` : ''}
                </span>
                <span className={`px-2 py-0.5 rounded-full font-medium ${
                  inv.status === 'paid' ? 'bg-green-100 text-green-800' :
                  inv.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                  inv.status === 'voided' ? 'bg-gray-100 text-gray-600' :
                  inv.status === 'overdue' ? 'bg-red-100 text-red-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {inv.status}
                </span>
              </div>
              <div className="mt-1 text-gray-600">
                ${inv.amount_paid.toFixed(2)} of ${inv.total_amount.toFixed(2)} paid
                {due > 0 && <span className="text-gray-900 font-medium"> · ${due.toFixed(2)} due</span>}
                {inv.due_date && <span> · due {formatDateDisplay(inv.due_date)}</span>}
              </div>
              {inv.payments && inv.payments.length > 0 && (
                <div className="mt-1 space-y-0.5 text-gray-500">
                  {inv.payments.map((p) => (
                    <div key={p.id}>
                      {p.payment_date ? formatDateDisplay(p.payment_date) : '—'} · {p.payment_method} · ${p.amount.toFixed(2)} · {p.status}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OptInNames({ enrollmentId, optInIds }: { enrollmentId: string; optInIds: string[] }) {
  const { data } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['admin-optin-names', enrollmentId, optInIds],
    queryFn: async () => {
      if (!optInIds?.length) return [];
      const { data } = await supabase
        .from('line_item_configs')
        .select('id, name')
        .in('id', optInIds);
      return (data as { id: string; name: string }[]) || [];
    },
    enabled: !!optInIds?.length,
  });

  if (!optInIds?.length) return <span className="text-xs text-gray-500">None</span>;

  const names = optInIds
    .map((id) => data?.find((d) => d.id === id)?.name || id)
    .join('; ');

  return (
    <span className="text-xs text-gray-900" title={names}>
      {names}
    </span>
  );
}

// OptionalItemsSelector — the add-ons this enrollment pays for
function OptionalItemsSelector({ 
  programId, 
  selectedOptInItems, 
  onChange,
}: { 
  programId: string; 
  selectedOptInItems: string[]; 
  onChange: (optInItems: string[]) => void; 
}) {
  type AvailableItem = {
    id: string;
    name: string;
    description?: string;
    type: string;
    amount: number;
  };

  const { data: availableItems, isLoading, isError } = useQuery<AvailableItem[]>({
    queryKey: ['program-line-items', programId],
    queryFn: async () => {
      if (!programId) return [] as AvailableItem[];

      const { data, error } = await supabase
        .from('pricing')
        .select(`
          amount,
          is_optional,
          line_item_configs:line_item_config_id (
            id,
            name,
            description,
            type
          )
        `)
        .eq('program_id', programId)
        .eq('is_optional', true);

      if (error) {
        throw new Error(error.message);
      }

      type RawRelation = { id?: string; name?: string; description?: string; type?: string };
      type RawRow = {
        amount?: number;
        is_optional?: boolean;
        line_item_configs?: RawRelation | RawRelation[];
      };

      const rows: RawRow[] = Array.isArray(data) ? (data as RawRow[]) : [];
      const items: AvailableItem[] = rows
        .map((row) => {
          const rel = row.line_item_configs;
          const cfg: RawRelation | undefined = Array.isArray(rel) ? rel[0] : rel;
          if (!cfg || !cfg.id || !cfg.name || !cfg.type || typeof row.amount !== 'number') {
            return null;
          }
          return {
            id: cfg.id,
            name: cfg.name,
            description: cfg.description,
            type: cfg.type,
            amount: row.amount,
          } as AvailableItem;
        })
        .filter((v): v is AvailableItem => v !== null);

      return items;
    },
    enabled: !!programId,
  });

  const handleToggleItem = (itemId: string) => {
    // Opt-in model: checking "Include" removes from opt-out list, unchecking adds
    const newSelection = selectedOptInItems.includes(itemId)
      ? selectedOptInItems.filter((id) => id !== itemId)
      : [...selectedOptInItems, itemId];
    onChange(newSelection);
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading optional items...</div>;
  }

  if (isError) {
    return <div className="text-sm text-red-600">Failed to load optional items</div>;
  }

  if (!availableItems || availableItems.length === 0) {
    return <div className="text-sm text-gray-500">No optional items available for this program</div>;
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-600">Check optional items to include in billing:</div>
      {availableItems.map((item) => (
        <label key={item.id} className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedOptInItems.includes(item.id)}
            onChange={() => handleToggleItem(item.id)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">{item.name}</div>
            {item.description && <div className="text-xs text-gray-500">{item.description}</div>}
            <div className="text-xs text-gray-500">{item.type} • ${item.amount.toFixed(2)}</div>
          </div>
        </label>
      ))}
      {availableItems.filter(item => selectedOptInItems.includes(item.id)).length > 0 && (
        <div className="text-xs text-blue-600">{availableItems.filter(item => selectedOptInItems.includes(item.id)).length} item(s) included</div>
      )}
    </div>
  );
}